import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import UserBar from '../components/UserBar';
import Header from '../components/Header';
import Hero from '../components/Hero';
import BackgroundVideo from '../components/BackgroundVideo';
import StatsBar from '../components/StatsBar';
import Features from '../components/Features';
import Packages from '../components/Packages';
import Roadmap from '../components/Roadmap';
import News from '../components/News';
import Testimonials from '../components/Testimonials';
import CTA from '../components/CTA';
import Footer from '../components/Footer';
import PortalModal from '../components/PortalModal';
import Dashboard from '../components/Dashboard';
import Investments from '../components/Investments';
import PortalCenter from '../components/PortalCenter';
import Profile from '../components/Profile';
import NewsPage from '../components/NewsPage';
import NewsDetailModal from '../components/NewsDetailModal';
import ChangePassword from '../components/ChangePassword';
import ShareTransfer from '../components/ShareTransfer';
import WelcomePopup from '../components/WelcomePopup';
import LiveChatWidget from '../components/LiveChatWidget';
import EmailVerifyBanner from '../components/EmailVerifyBanner';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';

const Home = () => {
  const [portalOpen, setPortalOpen] = useState(false);
  // Default view is ALWAYS 'home' — the cinematic landing page.
  // Dashboards open only when the user explicitly picks a nav item.
  const [view, setView] = useState('home');
  const [invTab, setInvTab] = useState('packages');
  const [invPkg, setInvPkg] = useState(null);
  const [portalInitialTab, setPortalInitialTab] = useState('signin');
  const [refCode, setRefCode] = useState('');
  const [emailNews, setEmailNews] = useState(null);
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const open = () => setPortalOpen(true);
  const close = () => setPortalOpen(false);

  // Detect referral link — /join?ref=SGW-XXXX or /?ref=SGW-XXXX
  useEffect(() => {
    const ref = (searchParams.get('ref') || '').trim();
    if (ref && !user) {
      setRefCode(ref);
      setPortalInitialTab('register');
      setPortalOpen(true);
    }
    // Deep-link from newsroom email — /?news=<uuid>
    const newsId = (searchParams.get('news') || '').trim();
    if (newsId) {
      (async () => {
        try {
          const { data } = await API.get(`/news/${newsId}`);
          if (data?.news) setEmailNews(data.news);
        } catch {}
      })();
    }
    // Deep-link from footer/menu on other pages — /?view=news or /?view=investments&sub=packages
    const viewParam = (searchParams.get('view') || '').trim();
    const subParam = (searchParams.get('sub') || '').trim();
    const sectionParam = (searchParams.get('section') || '').trim();
    if (viewParam) {
      setView(viewParam);
      if (viewParam === 'investments' && subParam) setInvTab(subParam);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (sectionParam) {
      // Wait for the section to render before scrolling
      setTimeout(() => {
        const el = document.getElementById(sectionParam);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goto = (v, sub) => {
    setView(v);
    if (v === 'investments' && sub) setInvTab(sub);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectPackage = (deckId) => {
    setInvPkg(deckId);
    setInvTab('packages');
    setView('investments');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showLanding = view === 'home';

  return (
    <div className={`min-h-screen relative ${showLanding ? 'bg-white' : 'bg-[#040d08]'}`}>
      {showLanding && <BackgroundVideo />}
      <div className="relative z-10">
        {user ? <UserBar /> : <TopBar onOpenPortal={open} />}
        {user && <EmailVerifyBanner />}
      <Header onOpenPortal={open} onNavigate={goto} />

      {showLanding ? (
        <>
          <Hero onOpenPortal={open} />
          <div className="relative bg-white">
            <StatsBar />
            <Features />
            <Packages onOpenPortal={open} onSelectPackage={selectPackage} />
            <Roadmap />
            <Testimonials />
            <News />
            {!user && <CTA />}
          </div>
        </>
      ) : (
        <>
          {view === 'partners' && <Dashboard />}
          {view === 'investments' && <Investments initialTab={invTab} initialPkg={invPkg} />}
          {view === 'portal-wallet' && <PortalCenter initialTab="wallet" />}
          {view === 'portal-deposit' && <PortalCenter initialTab="deposit" />}
          {view === 'portal-withdraw' && <PortalCenter initialTab="withdraw" />}
          {view === 'portal-transfer' && <PortalCenter initialTab="transfer" />}
          {view === 'portal-dividend' && <PortalCenter initialTab="dividend" />}
          {view === 'portal-history' && <PortalCenter initialTab="history" />}
          {view === 'profile-kyc' && <Profile initialTab="kyc" />}
          {view === 'profile-auth' && <Profile initialTab="auth" />}
          {view === 'profile-addr' && <Profile initialTab="addr" />}
          {view === 'news' && <NewsPage />}
          {view === 'share-transfer' && <ShareTransfer />}
          {view === 'change-password' && <ChangePassword />}
        </>
      )}

      <Footer onOpenPortal={open} onNavigate={goto} />
      <PortalModal open={portalOpen} onClose={close} initialTab={portalInitialTab} initialReferral={refCode} />
      {!user && !refCode && <WelcomePopup />}
      <LiveChatWidget />
      {emailNews && <NewsDetailModal news={emailNews} onClose={() => setEmailNews(null)} />}
      </div>
    </div>
  );
};

export default Home;
